import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Facebook, Mail, Phone, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const Footer1 = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [openSection, setOpenSection] = useState(null);

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setEmail('');
        }
    };

    const toggleSection = (section) => {
        setOpenSection(openSection === section ? null : section);
    };

    const shopLinks = [
        { label: 'Sarees', path: '/template1/shop' },
        { label: 'Lehengas', path: '/template1/shop' },
        { label: 'Kurtas', path: '/template1/shop' },
        { label: 'Accessories', path: '/template1/shop' },
        { label: 'New Arrivals', path: '/template1/shop' },
    ];

    const helpLinks = [
        { label: 'FAQs', path: '/template1/faqs' },
        { label: 'Shipping & Returns', path: '/template1/shipping-exchange' },
        { label: 'Privacy Policy', path: '/template1/privacy-policy' },
        { label: 'Terms & Conditions', path: '/template1/terms-conditions' },
        { label: 'Track Order', path: '/template1/orders' },
    ];

    const FooterSection = ({ title, links, sectionKey }) => (
        <div>
            {/* Desktop */}
            <div className="hidden md:block">
                <h4 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider mb-4">{title}</h4>
                <ul className="space-y-3">
                    {links.map((link) => (
                        <li key={link.label}>
                            <button
                                onClick={() => navigate(link.path)}
                                className="text-sm text-[#6B7280] hover:text-[#2D6A4F] transition-colors"
                            >
                                {link.label}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
            {/* Mobile: Accordion */}
            <div className="md:hidden border-b border-[#E5E7EB]">
                <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full flex items-center justify-between py-4 text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider"
                >
                    {title}
                    {openSection === sectionKey ? <ChevronUp size={18} className="text-[#6B7280]" /> : <ChevronDown size={18} className="text-[#6B7280]" />}
                </button>
                {openSection === sectionKey && (
                    <ul className="pb-4 space-y-3">
                        {links.map((link) => (
                            <li key={link.label}>
                                <button
                                    onClick={() => navigate(link.path)}
                                    className="text-sm text-[#6B7280] hover:text-[#2D6A4F] transition-colors"
                                >
                                    {link.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );

    return (
        <footer className="bg-[#F8FAF8] border-t border-[#E5E7EB]" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {/* Newsletter Bar */}
            <div className="bg-[#2D6A4F]">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
                        <div className="text-center md:text-left">
                            <h3 className="text-base md:text-lg font-semibold text-white mb-1">Stay in the Loop</h3>
                            <p className="text-sm text-white/70">Subscribe for new arrivals, offers & styling tips.</p>
                        </div>
                        <form onSubmit={handleSubscribe} className="flex w-full md:w-auto gap-2">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email address"
                                className="flex-1 md:w-64 px-4 py-2.5 text-sm bg-white/10 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:border-white transition-colors"
                                required
                            />
                            <button
                                type="submit"
                                className="px-6 py-2.5 bg-white text-[#2D6A4F] text-sm font-medium hover:bg-[#F8FAF8] transition-colors"
                            >
                                Subscribe
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Main Footer */}
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-14">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-8 lg:gap-12">
                    {/* Brand Column */}
                    <div className="mb-8 md:mb-0">
                        <h3 className="text-xl font-semibold text-[#2D6A4F] tracking-wider mb-3">PRIYA</h3>
                        <p className="text-sm text-[#6B7280] leading-relaxed mb-5">
                            Celebrating the timeless beauty of Indian craftsmanship. Curated ethnic fashion for the modern woman.
                        </p>
                        <div className="flex gap-3">
                            <a href="#" className="w-9 h-9 bg-[#2D6A4F]/10 hover:bg-[#2D6A4F] hover:text-white rounded-full flex items-center justify-center text-[#2D6A4F] transition-all duration-300">
                                <Instagram size={16} />
                            </a>
                            <a href="#" className="w-9 h-9 bg-[#2D6A4F]/10 hover:bg-[#2D6A4F] hover:text-white rounded-full flex items-center justify-center text-[#2D6A4F] transition-all duration-300">
                                <Facebook size={16} />
                            </a>
                        </div>
                    </div>

                    {/* Shop Links */}
                    <FooterSection title="Shop" links={shopLinks} sectionKey="shop" />

                    {/* Help Links */}
                    <FooterSection title="Help" links={helpLinks} sectionKey="help" />

                    {/* Contact */}
                    <div>
                        <div className="hidden md:block">
                            <h4 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider mb-4">Contact</h4>
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <Mail size={16} className="text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-[#6B7280]">hello@priya.com</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Phone size={16} className="text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-[#6B7280]">+91 98765 43210</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <MapPin size={16} className="text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-[#6B7280]">Mumbai, Maharashtra<br />India</span>
                                </div>
                            </div>
                        </div>
                        <div className="md:hidden border-b border-[#E5E7EB]">
                            <button
                                onClick={() => toggleSection('contact')}
                                className="w-full flex items-center justify-between py-4 text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider"
                            >
                                Contact
                                {openSection === 'contact' ? <ChevronUp size={18} className="text-[#6B7280]" /> : <ChevronDown size={18} className="text-[#6B7280]" />}
                            </button>
                            {openSection === 'contact' && (
                                <div className="pb-4 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Mail size={16} className="text-[#2D6A4F] flex-shrink-0" />
                                        <span className="text-sm text-[#6B7280]">hello@priya.com</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Phone size={16} className="text-[#2D6A4F] flex-shrink-0" />
                                        <span className="text-sm text-[#6B7280]">+91 98765 43210</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <MapPin size={16} className="text-[#2D6A4F] mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-[#6B7280]">Mumbai, Maharashtra, India</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-[#E5E7EB]">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-5">
                    <p className="text-center text-xs text-[#9CA3AF]">
                        &copy; {new Date().getFullYear()} PRIYA. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer1;
